import React, { useEffect, useRef, useState, useCallback } from 'react';
import Gantt from 'frappe-gantt';
import { Select, message, Skeleton, Radio, Badge, Tooltip } from 'antd';
import Text from 'antd/lib/typography/Text';
import { useProjectContext } from '../../../../../core/components/projects/useProjectContext';
import { useTaskContext } from '../../../../../core/components/tasks/useTaskContext';
import moment from 'moment';
import { space, text, link, accent, bg, border, radius, fontWeight as fontWeightTokens, fontSize as fontSizeTokens, fontFamily, hoverBg, shadow, duration } from '../../configs/designTokens';
import '../../styles/index.scss';
import useResponsiveTask from '../../hooks/useResponsiveTask';

const { Option } = Select;

const ProjectGantt = ({ currentProjectId }) => {
  const ganttRef = useRef(null);
  const ganttInstance = useRef(null);
  const [viewMode, setViewMode] = useState('Week');
  const dateChangeTimeoutRef = useRef(null);
  const progressChangeTimeoutRef = useRef(null);
  const { width: windowWidth, isMobile, isDesktop } = useResponsiveTask();

  const {
    currentProject,
    fetchMilestones,
    milestones,
    milestonesLoading,
    refetchProject,
    updateMilestone,
  } = useProjectContext();

  const { updateTask } = useTaskContext();

  // Refetch on mount or when project changes
  useEffect(() => {
    if (currentProjectId) {
      // Fetch project tasks
      if (refetchProject) {
        refetchProject();
      }

      // Fetch milestones
      fetchMilestones({
        variables: {
          where: { project: { id: currentProjectId } },
          orderBy: 'dueDate_ASC',
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentProjectId]);

  // Refetch when user returns to the tab/page
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && currentProjectId) {
        console.log('Tab became visible - refetching data');
        if (refetchProject) {
          refetchProject();
        }
        fetchMilestones({
          variables: {
            where: { project: { id: currentProjectId } },
            orderBy: 'dueDate_ASC',
          },
        });
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentProjectId]);

  const [taskLabels, setTaskLabels] = useState([]);
  const [ganttHeight, setGanttHeight] = useState(700);
  const sidebarRef = useRef(null);

  useEffect(() => {
    if (currentProjectId && viewMode) {
      if (refetchProject) {
        refetchProject();
      }
      fetchMilestones({
        variables: {
          where: { project: { id: currentProjectId } },
          orderBy: 'dueDate_ASC',
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode]);

  // Extract stable dependencies for useEffect
  const projectId = currentProject?.id;
  const milestonesCount = milestones?.length;

  const prepareGanttData = useCallback(() => {
    if (!currentProject || !milestones) {
      return { tasks: [], labels: [] };
    }

    const tasks = [];
    const labels = [];

    // Get all tasks assigned to milestones
    const tasksInMilestones = new Set();
    milestones.forEach(milestone => {
      milestone.tasks?.forEach(task => tasksInMilestones.add(task.id));
    });

    // Add all milestone groups with their tasks
    milestones.forEach((milestone, index) => {
      const milestoneTasks = milestone.tasks || [];

      if (milestoneTasks.length === 0) return;

      // Use milestone's own dates, not task dates
      // If milestone has startDate, use it; otherwise use earliest task start
      let milestoneStart = milestone.startDate ? moment(milestone.startDate) : null;
      let milestoneEnd = milestone.dueDate ? moment(milestone.dueDate) : null;

      // Fallback to task dates if milestone dates are missing
      if (!milestoneStart || !milestoneEnd) {
        const taskDates = milestoneTasks
          .map(t => ({
            start: t.startDate ? moment(t.startDate) : (t.createdAt ? moment(t.createdAt) : null),
            end: t.dueDate ? moment(t.dueDate) : null,
          }))
          .filter(d => d.start && d.end);

        if (taskDates.length === 0) return;

        if (!milestoneStart) {
          milestoneStart = moment.min(taskDates.map(d => d.start));
        }
        if (!milestoneEnd) {
          milestoneEnd = moment.max(taskDates.map(d => d.end));
        }
      }

      // Calculate completion percentage for milestone
      const completedCount = milestoneTasks.filter(t =>
        t.status === 'COMPLETED' || t.status === 'DONE'
      ).length;
      const completionPercent = milestoneTasks.length > 0
        ? Math.round((completedCount / milestoneTasks.length) * 100)
        : 0;

      // Add milestone header bar using milestone's own dates
      tasks.push({
        id: `milestone-header-${milestone.id}`,
        name: `━━━ ${milestone.name} (${completionPercent}% Complete) ━━━`,
        start: milestoneStart.format('YYYY-MM-DD'),
        end: milestoneEnd.format('YYYY-MM-DD'),
        progress: completionPercent,
        custom_class: `milestone-header-color-${(index % 5) + 1}`,
        is_milestone_header: true,
        milestone_id: milestone.id,
        milestone_index: index,
      });

      // Add milestone header label
      labels.push({
        id: `milestone-header-${milestone.id}`,
        text: `📍 ${milestone.name}`,
        type: 'milestone-header',
        completionPercent: completionPercent
      });

      // Add milestone tasks
      milestoneTasks.forEach((task, taskIndex) => {
        const startDate = task.startDate ? moment(task.startDate) : (task.createdAt ? moment(task.createdAt) : moment());
        const endDate = task.dueDate ? moment(task.dueDate) : moment().add(7, 'days');

        const completedStatuses = ['COMPLETED', 'DONE'];
        const progress = completedStatuses.includes(task.status) ? 100 : 0;

        // Get assignee names
        const assigneeNames = task.assigneeUsers?.map(u => u.name || u.username).join(', ') || '';

        tasks.push({
          id: task.id,
          name: `  ${taskIndex + 1}. ${task.name}`,
          task_name: task.name, // Original task name for popup
          task_status: task.status,
          assignees: assigneeNames,
          start: startDate.format('YYYY-MM-DD'),
          end: endDate.format('YYYY-MM-DD'),
          progress: progress,
          custom_class: `milestone-task-color-${(index % 5) + 1}`,
          milestone_name: milestone.name,
          milestone_id: milestone.id,
          milestone_index: index,
        });

        // Add task label
        labels.push({
          id: task.id,
          text: task.name,
          type: 'task',
          status: task.status
        });
      });

      // Add section separator (invisible spacer) after each milestone except the last
      if (index < milestones.length - 1) {
        tasks.push({
          id: `separator-${milestone.id}`,
          name: '',
          start: moment().format('YYYY-MM-DD'),
          end: moment().format('YYYY-MM-DD'),
          progress: 0,
          custom_class: `milestone-separator-${index}`,
          is_separator: true,
        });

        // Add separator label
        labels.push({
          id: `separator-${milestone.id}`,
          text: '',
          type: 'separator'
        });
      }
    });

    const unassignedTasks = (currentProject.tasks || []).filter(task => !tasksInMilestones.has(task.id));

    if (unassignedTasks.length > 0) {
      if (milestones.length > 0) {
        tasks.push({
          id: `separator-unassigned`,
          name: '',
          start: moment().format('YYYY-MM-DD'),
          end: moment().format('YYYY-MM-DD'),
          progress: 0,
          custom_class: `milestone-separator-unassigned`,
          is_separator: true,
        });

        labels.push({
          id: `separator-unassigned`,
          text: '',
          type: 'separator'
        });
      }

      const completedCount = unassignedTasks.filter(t =>
        t.status === 'COMPLETED' || t.status === 'DONE'
      ).length;
      const completionPercent = Math.round((completedCount / unassignedTasks.length) * 100);

      const unassignedTaskDates = unassignedTasks
        .map(t => ({
          start: t.startDate ? moment(t.startDate) : (t.createdAt ? moment(t.createdAt) : null),
          end: t.dueDate ? moment(t.dueDate) : null,
        }))
        .filter(d => d.start && d.end);

      if (unassignedTaskDates.length > 0) {
        const unassignedStart = moment.min(unassignedTaskDates.map(d => d.start));
        const unassignedEnd = moment.max(unassignedTaskDates.map(d => d.end));

        tasks.push({
          id: `project-header-unassigned`,
          name: `━━━ Other Tasks (${completionPercent}% Complete) ━━━`,
          start: unassignedStart.format('YYYY-MM-DD'),
          end: unassignedEnd.format('YYYY-MM-DD'),
          progress: completionPercent,
          custom_class: 'project-header',
          is_project_header: true,
        });

        labels.push({
          id: `project-header-unassigned`,
          text: `📋 Other Tasks`,
          type: 'project-header',
          completionPercent: completionPercent
        });
      }

      // Add other tasks
      unassignedTasks.forEach((task, taskIndex) => {
        const startDate = task.startDate ? moment(task.startDate) : (task.createdAt ? moment(task.createdAt) : null);
        const endDate = task.dueDate ? moment(task.dueDate) : null;

        if (!startDate || !endDate) return;

        const completedStatuses = ['COMPLETED', 'DONE'];
        const progress = completedStatuses.includes(task.status) ? 100 : 0;

        const assigneeNames = task.assigneeUsers?.map(u => u.name || u.username).join(', ') || '';

        tasks.push({
          id: task.id,
          name: `  ${taskIndex + 1}. ${task.name}`,
          task_name: task.name,
          task_status: task.status,
          assignees: assigneeNames,
          start: startDate.format('YYYY-MM-DD'),
          end: endDate.format('YYYY-MM-DD'),
          progress: progress,
          custom_class: 'project-task',
          is_unassigned: true,
        });

        labels.push({
          id: task.id,
          text: task.name,
          type: 'project-task',
          status: task.status
        });
      });
    }

    return { tasks, labels };
  }, [currentProject, milestones]);

  useEffect(() => {
    const ganttElement = ganttRef.current;
    if (!ganttElement) return;

    // Capture ref values for cleanup function
    const dateChangeTimeout = dateChangeTimeoutRef.current;
    const progressChangeTimeout = progressChangeTimeoutRef.current;

    const { tasks, labels } = prepareGanttData();

    setTaskLabels(labels);

    const rowHeight = 64;
    const headerHeight = 140; 
    const calculatedHeight = Math.max(400, Math.min(1200, tasks.length * rowHeight + headerHeight));
    setGanttHeight(calculatedHeight);

    if (tasks.length === 0) {
      if (ganttInstance.current) {
        ganttInstance.current = null;
      }
      ganttElement.innerHTML = `<div style="padding: ${space[10]}px; text-align: center; color: ${text.tertiary}; font-size: ${fontSizeTokens.button}px;">No tasks with valid dates found. Tasks need both a start date and due date to appear in the Gantt chart.</div>`;
      return;
    }

    // Fixed date range: 2024-2027 only
    const dateRange = {
      start: moment('2024-01-01'),
      end: moment('2027-12-31')
    };

    // Adjust task dates to fit within the fixed range
    const adjustedTasks = tasks.map(task => {
      const taskStart = moment(task.start);
      const taskEnd = moment(task.end);

      return {
        ...task,
        start: taskStart.isBefore(dateRange.start) ? dateRange.start.format('YYYY-MM-DD') : task.start,
        end: taskEnd.isAfter(dateRange.end) ? dateRange.end.format('YYYY-MM-DD') : task.end,
      };
    });

    // Clear previous instance
    if (ganttInstance.current) {
      ganttInstance.current = null;
    }

    // Clear the container
    ganttElement.innerHTML = '';

    // Create new instance
    try {
      ganttInstance.current = new Gantt(ganttElement, adjustedTasks, {
        view_mode: viewMode,
        view_start: dateRange.start.format('YYYY-MM-DD'),
        view_end: dateRange.end.format('YYYY-MM-DD'),
        bar_height: 40,
        bar_corner_radius: 4,
        arrow_curve: 5,
        padding: space[6],
        container_height: 'auto',
        date_format: 'YYYY-MM-DD',
        language: 'en',
        popup_trigger: 'none', 
        on_date_change: (task, start, end) => {
          if (task.is_project_header) {
            message.destroy();
            message.info('This is a section header for other tasks and cannot be dragged');
            return;
          }
          if (task.id === 'separator-unassigned') {
            message.destroy();
            message.info('This is just a separator between sections');
            return;
          }

          // Handle milestone header drag
          if (task.is_milestone_header) {
            const oldStart = moment(task._start);
            const oldEnd = moment(task._end);
            const newStart = moment(start);
            const newEnd = moment(end);

            const originalDuration = oldEnd.diff(oldStart, 'days');
            const startDiff = newStart.diff(oldStart, 'days');
            const endDiff = newEnd.diff(oldEnd, 'days');
            const isBarDrag = Math.abs(startDiff - endDiff) <= 1 && startDiff !== 0;

            let potentialStart = newStart.clone();
            let potentialEnd = newEnd.clone();

            if (isBarDrag) {
              potentialStart = newStart.clone();
              potentialEnd = newStart.clone().add(originalDuration, 'days');
            }

            if (potentialStart.isSameOrAfter(potentialEnd, 'day')) {
              message.destroy();
              message.error('Milestone due date must be after start date. They cannot be on the same day.');
              if (refetchProject) {
                refetchProject();
              }
              return;
            }

            let finalStart = potentialStart.clone();
            let finalEnd = potentialEnd.clone();

            task._start = finalStart.toDate();
            task._end = finalEnd.toDate();
            task.start = finalStart.format('YYYY-MM-DD');
            task.end = finalEnd.format('YYYY-MM-DD');

            // Clear any existing timeout
            if (dateChangeTimeoutRef.current) {
              clearTimeout(dateChangeTimeoutRef.current);
            }

            // Update milestone dates
            dateChangeTimeoutRef.current = setTimeout(async () => {
              try {
                const updateData = {
                  startDate: finalStart.toISOString(),
                  dueDate: finalEnd.toISOString(),
                };

                await updateMilestone({
                  variables: {
                    data: updateData,
                    where: { id: task.milestone_id },
                  },
                });

                message.success('Milestone dates updated', 1);
              } catch (error) {
                console.error('Failed to update milestone dates:', error);
                message.error('Failed to update milestone dates');
              }
            }, 500);
            return;
          }

          // Ignore separator
          if (task.is_separator) return;

          const oldStart = moment(task._start);
          const oldEnd = moment(task._end);
          const newStart = moment(start);
          const newEnd = moment(end);

          const originalDuration = Math.max(1, oldEnd.diff(oldStart, 'days'));

          // Check if this is a whole-bar drag (both dates changed by the same amount)
          // or a resize operation (only one end changed)
          const startDiff = newStart.diff(oldStart, 'days');
          const endDiff = newEnd.diff(oldEnd, 'days');
          const isBarDrag = Math.abs(startDiff - endDiff) <= 1 && startDiff !== 0;

          let finalStart = newStart.clone();
          let finalEnd = newEnd.clone();

          if (isBarDrag) {
            finalStart = newStart.clone();
            finalEnd = newStart.clone().add(originalDuration, 'days');
          } else {
            if (finalEnd.diff(finalStart, 'days') < 1) {
              finalEnd = finalStart.clone().add(1, 'days');
            }
          }

          if (finalStart.isSameOrAfter(finalEnd, 'day')) {
            message.destroy();
            message.error('Due date must be after start date. They cannot be on the same day.');
            if (refetchProject) {
              refetchProject();
            }
            return;
          }

          console.log('Gantt date change:', {
            taskId: task.id,
            taskName: task.name,
            oldStart: oldStart.format('YYYY-MM-DD'),
            newStart: newStart.format('YYYY-MM-DD'),
            oldEnd: oldEnd.format('YYYY-MM-DD'),
            newEnd: newEnd.format('YYYY-MM-DD'),
            finalStart: finalStart.format('YYYY-MM-DD'),
            finalEnd: finalEnd.format('YYYY-MM-DD'),
            startDiff,
            endDiff,
            isBarDrag,
            originalDuration,
            validation: `${finalStart.format('YYYY-MM-DD')} >= ${finalEnd.format('YYYY-MM-DD')} ? ${finalStart.isSameOrAfter(finalEnd, 'day')}`
          });

          task._start = finalStart.toDate();
          task._end = finalEnd.toDate();
          task.start = finalStart.format('YYYY-MM-DD');
          task.end = finalEnd.format('YYYY-MM-DD');

          // Clear any existing timeout
          if (dateChangeTimeoutRef.current) {
            clearTimeout(dateChangeTimeoutRef.current);
          }

          dateChangeTimeoutRef.current = setTimeout(async () => {
            try {

              const updateData = {
                startDate: finalStart.toISOString(),
                dueDate: finalEnd.toISOString(),
              };

              console.log('Updating task with:', updateData);

              // Update the task in the background without refetching
              // This keeps the UI smooth and prevents reload/flash
              await updateTask({
                variables: {
                  data: updateData,
                  where: { id: task.id },
                },
              });

              // Show subtle success message
              message.success('Task dates updated', 1);

              // NO REFETCH - UI already updated optimistically by the drag
              // The chart stays smooth without reloading
            } catch (error) {
              console.error('Failed to update task dates:', error);
              message.error('Failed to update task dates');
            }
          }, 500); // Wait 500ms after user stops dragging
        },
      });

      const ganttContainer = ganttElement.querySelector('.gantt-container');
      const ganttSvg = ganttContainer?.querySelector('svg');
      if (ganttSvg) {
        const currentHeight = parseInt(ganttSvg.getAttribute('height') || '0');
        ganttSvg.setAttribute('height', (currentHeight + 50).toString());
      }

      const createCustomPopup = (task, event) => {
        const existingPopup = document.querySelector('.custom-gantt-popup');
        if (existingPopup) {
          existingPopup.remove();
        }

        if (task.is_milestone_header || task.is_separator || task.is_project_header) {
          return;
        }

        const popup = document.createElement('div');
        popup.className = 'custom-gantt-popup';

        const durationDays = moment(task.end).diff(moment(task.start), 'days');

        popup.innerHTML = `
          <div style="background: ${bg.card}; border: 1px solid ${border.gray}; border-radius: ${radius.md}px; padding: ${space[3]}px; box-shadow: ${shadow.md}; min-width: 250px; font-family: ${fontFamily.base};">
            <div style="font-weight: ${fontWeightTokens.semibold}; font-size: ${fontSizeTokens.button}px; color: ${text.heading}; margin-bottom: ${space[2]}px; padding-bottom: ${space[2]}px; border-bottom: 1px solid ${border.gray};">
              ${task.task_name || task.name}
            </div>
            <div style="font-size: ${fontSizeTokens.meta}px; color: ${text.body}; line-height: 1.6;">
              <div style="margin-bottom: ${space[1]}px;">
                <strong style="color: ${text.secondary};">Start:</strong> ${moment(task.start).format('MMM D, YYYY')}
              </div>
              <div style="margin-bottom: ${space[1]}px;">
                <strong style="color: ${text.secondary};">Due:</strong> ${moment(task.end).format('MMM D, YYYY')}
              </div>
              <div style="margin-bottom: ${space[1]}px;">
                <strong style="color: ${text.secondary};">Duration:</strong> ${durationDays} days
              </div>
              <div style="margin-bottom: ${space[1]}px;">
                <strong style="color: ${text.secondary};">Progress:</strong> ${task.progress}%
              </div>
              ${task.assignees ? `<div style="margin-bottom: ${space[1]}px;"><strong style="color: ${text.secondary};">Assigned to:</strong> ${task.assignees}</div>` : ''}
            </div>
          </div>
        `;

        popup.style.position = 'absolute';
        popup.style.left = `${event.pageX + 10}px`;
        popup.style.top = `${event.pageY + 10}px`;
        popup.style.zIndex = '10000';

        document.body.appendChild(popup);

        const closePopup = (e) => {
          if (!popup.contains(e.target)) {
            popup.remove();
            document.removeEventListener('click', closePopup);
          }
        };
        setTimeout(() => {
          document.addEventListener('click', closePopup);
        }, 100);
      };

      const barWrappers = ganttElement.querySelectorAll('.bar-wrapper');
      barWrappers.forEach((barWrapper, index) => {
        const task = adjustedTasks[index];
        if (task) {
          barWrapper.addEventListener('click', (e) => {
            createCustomPopup(task, e);
          });
        }
      });

      // Sync scrolling between sidebar and Gantt chart (vertical only)
      if (ganttContainer && sidebarRef.current) {
        let ticking = false;

        const syncScroll = () => {
          const scrollTop = ganttContainer.scrollTop;

          if (!ticking) {
            window.requestAnimationFrame(() => {
              // Only sync vertical scrolling, not horizontal
              if (sidebarRef.current) {
                sidebarRef.current.scrollTop = scrollTop;
              }
              ticking = false;
            });
            ticking = true;
          }
        };

        // Allow vertical mousewheel scroll, but prevent horizontal (only horizontal scrollbar slider works)
        const preventHorizontalWheel = (e) => {
          // If user is scrolling vertically (shift not pressed), allow it
          if (Math.abs(e.deltaY) > Math.abs(e.deltaX) && !e.shiftKey) {
            // Allow vertical scroll - do nothing
            return;
          }
          // Prevent horizontal scroll (shift+wheel or trackpad horizontal swipe)
          e.preventDefault();
          e.stopPropagation();
        };

        // Use passive listener to improve scroll performance
        ganttContainer.addEventListener('scroll', syncScroll, { passive: true });

        // Prevent horizontal mousewheel scroll (non-passive to allow preventDefault)
        ganttContainer.addEventListener('wheel', preventHorizontalWheel, { passive: false });

        return () => {
          ganttContainer.removeEventListener('scroll', syncScroll);
          ganttContainer.removeEventListener('wheel', preventHorizontalWheel);
        };
      }
    } catch (error) {
      console.error('Failed to initialize Gantt chart:', error);
    }

    return () => {
      // Clear any pending timeouts using captured values
      if (dateChangeTimeout) {
        clearTimeout(dateChangeTimeout);
      }
      if (progressChangeTimeout) {
        clearTimeout(progressChangeTimeout);
      }

      if (ganttInstance.current) {
        ganttInstance.current = null;
      }
      if (ganttElement) {
        ganttElement.innerHTML = '';
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prepareGanttData, viewMode, projectId, milestonesCount]);
  // Note: Excluding updateTask, refetchProject, fetchMilestones, and currentProjectId from dependencies
  // because they're used inside closures within the Gantt event handlers. Including them would cause
  // unnecessary chart recreation on every render.


  const getProjectTasksCount = () => {
    if (!currentProject || !milestones) return 0;

    const tasksInMilestones = new Set();
    milestones.forEach(milestone => {
      milestone.tasks?.forEach(task => tasksInMilestones.add(task.id));
    });

    return currentProject.tasks?.filter(task => !tasksInMilestones.has(task.id)).length || 0;
  };

  if (milestonesLoading) {
    return (
      <div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: space[5],
          }}
        >
          <Text strong style={{ fontSize: windowWidth < 768 ? fontSizeTokens.title : fontSizeTokens.title, color: text.darkAlt }}>Project Gantt Chart</Text>
        </div>
        <Skeleton active paragraph={{ rows: 8 }} />
      </div>
    );
  }

  const hasTasks = (currentProject?.tasks?.length || 0) > 0;

  if (!hasTasks) {
    return (
      <div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: space[5],
          }}
        >
          <Text strong style={{ fontSize: windowWidth < 768 ? fontSizeTokens.title : fontSizeTokens.title, color: text.darkAlt }}>Project Gantt Chart</Text>
        </div>
        <div className="project-gantt__empty">
          <Text className="project-gantt__empty-text">
            No tasks available to display in Gantt chart
          </Text>
        </div>
      </div>
    );
  }

  const unassignedTasksCount = getProjectTasksCount();

  return (
    <div>
      {unassignedTasksCount > 0 && (
        <div className="project-gantt__warning">
          <Text className="project-gantt__warning-text">
            {unassignedTasksCount} task{unassignedTasksCount > 1 ? 's are' : ' is'} displayed in "Other Tasks" section below.
          </Text>
        </div>
      )}

      {/* Header */}
      <div
        style={{
          display: 'flex',
          flexDirection: windowWidth < 768 ? 'column' : 'row',
          justifyContent: 'space-between',
          alignItems: windowWidth < 768 ? 'flex-start' : 'center',
          marginBottom: space[4],
          paddingBottom: space[4],
          borderBottom: `1px solid ${border.gray}`,
          gap: windowWidth < 768 ? space[3] : 0,
        }}
      >
        <div className="project-gantt__title-row">
          <Text strong className="project-gantt__title" style={{ fontSize: windowWidth < 768 ? fontSizeTokens.title : fontSizeTokens.title }}>Timeline</Text>
        </div>

        {/* Asana-style Controls */}
        <div
          style={{
            display: 'flex',
            flexDirection: windowWidth < 768 ? 'column' : 'row',
            alignItems: windowWidth < 768 ? 'flex-start' : 'center',
            gap: windowWidth < 768 ? space[2] : space[4],
            width: windowWidth < 768 ? '100%' : 'auto',
          }}
        >
          {/* Zoom Controls */}
          <div
            style={{
              display: 'flex',
              flexDirection: windowWidth < 768 ? 'column' : 'row',
              alignItems: windowWidth < 768 ? 'flex-start' : 'center',
              gap: space[2.5],
              width: windowWidth < 768 ? '100%' : 'auto',
            }}
          >
            <Text className="project-gantt__zoom-label">Zoom:</Text>
            <Radio.Group
              value={viewMode}
              onChange={(e) => setViewMode(e.target.value)}
              buttonStyle="solid"
            >
              <Radio.Button
                value="Day"
                style={{
                  fontSize: windowWidth < 768 ? fontSizeTokens.caption : fontSizeTokens.button,
                  height: space[9],
                  padding: windowWidth < 768 ? `0 ${space[2]}px` : `0 ${space[4]}px`,
                  lineHeight: `${space[9] - 2}px`,
                  fontWeight: fontWeightTokens.medium,
                  flex: windowWidth < 768 ? 1 : 'none',
                }}
              >
                Days
              </Radio.Button>
              <Radio.Button
                value="Week"
                style={{
                  fontSize: windowWidth < 768 ? fontSizeTokens.caption : fontSizeTokens.button,
                  height: space[9],
                  padding: windowWidth < 768 ? `0 ${space[2]}px` : `0 ${space[4]}px`,
                  lineHeight: `${space[9] - 2}px`,
                  fontWeight: fontWeightTokens.medium,
                  flex: windowWidth < 768 ? 1 : 'none',
                }}
              >
                Weeks
              </Radio.Button>
            </Radio.Group>
          </div>
        </div>
      </div>


      {/* Gantt Chart with Sidebar */}
      <div
        style={{
          backgroundColor: bg.card,
          border: `1px solid ${border.gray}`,
          borderRadius: radius.md,
          padding: windowWidth < 768 ? space[3] : space[5],
          display: 'flex',
          flexDirection: windowWidth < 768 ? 'column' : 'row',
          gap: windowWidth < 768 ? space[4] : space[0],
          position: 'relative',
          height: windowWidth < 768 ? 'auto' : `${ganttHeight}px`,
          overflow: windowWidth < 768 ? 'visible' : 'hidden',
        }}
      >
        {/* Task Labels Sidebar */}
        <div
          ref={sidebarRef}
          style={{
            width: windowWidth < 768 ? '100%' : '280px',
            flexShrink: 0,
            borderRight: windowWidth < 768 ? 'none' : `2px solid ${border.gray}`,
            borderBottom: windowWidth < 768 ? `2px solid ${border.gray}` : 'none',
            paddingRight: windowWidth < 768 ? space[0] : space[4],
            paddingBottom: windowWidth < 768 ? space[4] : '50px',
            paddingTop: windowWidth < 768 ? space[0] : '85px',
            overflow: windowWidth < 768 ? 'visible' : 'auto',
            height: windowWidth < 768 ? 'auto' : '100%',
            scrollbarWidth: 'none', // Firefox
            msOverflowStyle: 'none', // IE/Edge
          }}
          className="gantt-sidebar"
        >
          {taskLabels.map((label) => {
            if (label.type === 'milestone-header') {
              return (
                <div
                  key={label.id}
                  style={{
                    height: space[16],
                    display: 'flex',
                    alignItems: 'center',
                    fontWeight: fontWeightTokens.semibold,
                    fontSize: fontSizeTokens.button,
                    color: link.blueText,
                    backgroundColor: hoverBg.blue,
                    padding: `0 ${space[3]}px`,
                    borderRadius: radius.xs,
                    marginBottom: space[0]
                  }}
                >
                  {label.text}
                  <span style={{ marginLeft: 'auto', fontSize: fontSizeTokens.caption, color: text.secondary }}>
                    {label.completionPercent}%
                  </span>
                </div>
              );
            } else if (label.type === 'project-header') {
              return (
                <div
                  key={label.id}
                  style={{
                    height: space[16],
                    display: 'flex',
                    alignItems: 'center',
                    fontWeight: fontWeightTokens.semibold,
                    fontSize: fontSizeTokens.button,
                    color: accent.sky800,
                    backgroundColor: hoverBg.sky,
                    padding: `0 ${space[3]}px`,
                    borderRadius: radius.xs,
                    marginBottom: space[0]
                  }}
                >
                  {label.text}
                </div>
              );
            } else if (label.type === 'task' || label.type === 'project-task') {
              const isCompleted = label.status === 'COMPLETED' || label.status === 'DONE';
              return (
                <a
                  key={label.id}
                  href={`/tasks/details/${label.id}`}
                  style={{
                    height: space[16],
                    display: 'flex',
                    alignItems: 'center',
                    fontSize: fontSizeTokens.meta,
                    color: isCompleted ? text.tertiary : text.body,
                    padding: `0 ${space[3]}px 0 ${space[6]}px`,
                    textDecoration: 'none',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    cursor: 'pointer',
                    transition: `background-color ${duration.base} ease`
                  }}
                  title={label.text}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = hoverBg.blue;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <span style={{
                    width: '6px',
                    height: '6px',
                    borderRadius: '50%',
                    backgroundColor: isCompleted ? accent.emerald : link.blue,
                    marginRight: space[2],
                    flexShrink: 0
                  }} />
                  <span style={{
                    textDecoration: isCompleted ? 'line-through' : 'none',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    {label.text}
                  </span>
                </a>
              );
            } else if (label.type === 'separator') {
              return (
                <div
                  key={label.id}
                  style={{
                    height: space[16]
                  }}
                />
              );
            }
            return null;
          })}
        </div>

        {/* Gantt Timeline */}
        <div
          ref={ganttRef}
          style={{
            flex: 1,
            position: 'relative',
            minWidth: 0,
            height: windowWidth < 768 ? '500px' : '100%',
            overflow: windowWidth < 768 ? 'auto' : 'hidden',
          }}
        />
      </div>

    </div>
  );
};

export default ProjectGantt;
